// Колоните на справка „Фактури".
//
// КЛИЕНТСКИ режим: `GET /invoices` връща целия период наведнъж (масив, без
// страниране), затова сортирането, филтрите в колоните и редът „Общо" се
// смятат в браузъра. Двата чеклиста (вид, състояние) обаче носят ИЗРИЧНИ
// опции — причината е в `chipOptions` по-долу.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { EM_DASH } from "../../../../components/dataTable";
import type {
  ChecklistOption,
  ChipCellValue,
} from "../../../../components/dataTable";
import type { InvoiceLabels, InvoiceRow } from "./invoicesMath";

const helper = createColumnHelper<InvoiceRow>();

/**
 * Датата се форматира РЪЧНО. `toLocaleDateString("bg-BG")` лепи „ г." и редът
 * се пренася на два реда в клетката.
 */
const formatIssueDate = (iso: string): string => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : EM_DASH;
};

/**
 * Опциите на чеклиста върху колона с ЧИП.
 *
 * В клиентски режим чеклистът обикновено вади стойностите си от фасетите, но
 * фасетите на такава колона са ключове-ОБЕКТИ (`{tone, label}`) — списъкът се
 * подрежда с `localeCompare` върху обект и пада. Затова опциите тук са
 * изрични, а `value` Е ЕТИКЕТЪТ: `checklistFilterFn` сравнява точно него
 * (`facetValueOf` чете `.label` на обектните клетки).
 */
const chipOptions = (...labels: string[]): ChecklistOption[] =>
  labels.map((label) => ({ value: label, label }));

export const useInvoicesColumns = (labels: InvoiceLabels) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor((row) => row.number, {
        id: "number",
        header: t("invoicesReport.colNumber"),
        size: 130,
        // Собствен рендер: номерът е ЗНАК, не сума — моно, за да се сверява
        // цифра по цифра, но подравнен вляво, за да не застане между парите.
        // Анулираната се зачертава още тук: тонът на реда се губи под курсора
        // и на телефона, а зачертаният номер остава.
        cell: (context) => (
          <span
            className={`font-pos-mono text-[12.5px] font-bold tabular-nums ${
              context.row.original.isCancelled
                ? "text-danger-ink line-through"
                : "text-ink"
            }`}
          >
            {context.getValue()}
          </span>
        ),
        meta: {
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.issueDate, {
        id: "issueDate",
        header: t("invoicesReport.colDate"),
        size: 118,
        // Клетката НЕ е `dt`: датата на издаване е ДЕН без час и `dt` би
        // изписала едро „00:00", а датата — дребно под него. Подредбата тече
        // върху суровия ISO низ, за който лексикографското сравнение Е
        // хронологично.
        cell: (context) => (
          <span className="font-pos-mono text-[12.5px] font-semibold tabular-nums text-ink">
            {formatIssueDate(context.getValue())}
          </span>
        ),
        meta: {
          filter: { kind: "dateRange" },
          excel: { format: "date" },
        },
      }),

      // Часът на СЪСТАВЯНЕ — рядко нужен, но иначе няма откъде да се вземе.
      // Изгасен по подразбиране: стои в панела „Колони" и се пали с отметка,
      // а в Excel излиза винаги.
      helper.accessor((row) => row.createdAt, {
        id: "createdAt",
        header: t("invoicesReport.colTime"),
        size: 96,
        // Без дата под часа — колоната „Дата" е точно до него.
        meta: {
          cell: { kind: "dt", withDate: "never" },
          defaultHidden: true,
          excel: { format: "date" },
        },
      }),

      helper.accessor((row) => row.customerName, {
        id: "customerName",
        header: t("invoicesReport.colCustomer"),
        size: 240,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.customerEik, {
        id: "customerEik",
        header: t("invoicesReport.colEik"),
        size: 132,
        // ЕИК-ът е идентификатор, не сума: моно (сверява се цифра по цифра),
        // но подравнен вляво, при името, на което принадлежи.
        cell: (context) => {
          const eik = context.getValue();
          if (!eik) return <span className="text-faint">{EM_DASH}</span>;
          return (
            <span className="font-pos-mono text-[12.5px] font-medium tabular-nums text-soft">
              {eik}
            </span>
          );
        },
        meta: {
          filter: { kind: "text" },
          hideBelow: "narrow",
          excel: { format: "text" },
        },
      }),

      helper.accessor(
        (row): ChipCellValue => ({
          // „Опростена" е рядката форма и трябва да се забелязва; „Данъчна" е
          // ежедневието и стои тихо, за да не стане колоната цветна стена.
          tone: row.isSimplified ? "brand" : "neutral",
          label: row.typeLabel,
        }),
        {
          id: "type",
          header: t("reportsPage.invoicesColType"),
          size: 124,
          meta: {
            cell: { kind: "chip" },
            filter: {
              kind: "checklist",
              options: chipOptions(labels.tax, labels.simplified),
            },
            hideBelow: "narrow",
            excel: { format: "text" },
          },
        },
      ),

      // Заместникът на отметката „Само анулирани" над таблицата: филтърът стои
      // в колоната, която показва стойността, и работи и в обратната посока
      // („само издадените"). „Издадена" е неутрален чип — колона от зелени
      // баджове върху всеки ред не носи информация.
      helper.accessor(
        (row): ChipCellValue => ({
          tone: row.isCancelled ? "danger" : "neutral",
          label: row.statusLabel,
        }),
        {
          id: "status",
          header: t("reportsPage.invoicesColStatus"),
          size: 130,
          meta: {
            cell: { kind: "chip" },
            filter: {
              kind: "checklist",
              options: chipOptions(labels.issued, labels.cancelled),
            },
            mobileRole: "meta",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.taxable, {
        id: "taxable",
        header: t("invoicesReport.colTaxable"),
        size: 140,
        meta: {
          cell: { kind: "money", mutedZero: true },
          filter: { kind: "numberRange" },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.vat, {
        id: "vat",
        header: t("invoicesReport.colVat"),
        size: 124,
        meta: {
          cell: { kind: "money", mutedZero: true },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),

      // Ставката, която стоеше на екрана досега. Изгасена по подразбиране: за
      // почти всички фактури тя е една и съща и колоната повтаря себе си ред
      // след ред — но когато трябва (смесени ставки, нулева основа), няма
      // откъде другаде да се вземе. БЕЗ `total` — процентите не се сумират.
      helper.accessor((row) => row.vatRate, {
        id: "vatRate",
        header: t("invoicesReport.colVatRate"),
        size: 104,
        meta: {
          cell: { kind: "pct", decimals: 0 },
          defaultHidden: true,
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.nonTaxable, {
        id: "nonTaxable",
        header: t("invoicesReport.colNonTaxable"),
        size: 140,
        meta: {
          cell: { kind: "money", mutedZero: true },
          total: "sum",
          defaultHidden: true,
          excel: { format: "money" },
        },
      }),

      helper.accessor((row) => row.grandTotal, {
        id: "grandTotal",
        header: t("invoicesReport.colGrand"),
        size: 144,
        meta: {
          cell: { kind: "money", strong: true },
          filter: { kind: "numberRange" },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),
    ],
    [t, labels],
  );
};
